import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './utils/FirebaseConfig';
import { syncEtikafAccount } from './utils/accountRoles';

import Message from './pages/Message';
import Login from './pages/Login';
import Utama from './pages/Calendar';
import Dashboard from './pages/Dashboard';
import SystemUpdates from './pages/SystemUpdates';
import Sidebar from './components/Sidebar';
import Etv from './pages/Etv';
import Profail from './pages/Profail';
import Status from './pages/Status';
import PaymentSuccess from './pages/PaymentSuccess';
import SubscriptionExpired from './pages/SubscriptionExpired';
import Asatizah from './pages/Asatizah';
import Daftar from './pages/Daftar';
import NoEtv from "./pages/NoEtv";
import Result from "./pages/Result";
import Kitab from "./pages/Kitab";
import Kempen from "./pages/Kempen";


import ErrorBoundary from './components/ErrorBoundary';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [mosqueId, setMosqueId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionValid, setSubscriptionValid] = useState(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState(null);
  const [etvUser, setEtvUser] = useState(false);
  const [kempenMode, setKempenMode] = useState(false);

  // 📱 Toggle Sidebar
  const toggleSidebar = () => setIsOpen(!isOpen);

  // 📏 Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ Main Authentication & User Sync
  useEffect(() => {

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        console.log("❌ No user logged in. Redirecting...");
        setIsAuthenticated(false);
        setSubscriptionValid(false);
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userProfile');
        setLoading(false);
        return;
      }

      // 🧭 Ambil data dari localStorage (new or old)
      const storedProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");

      let storedMosqueId = storedProfile.mosqueId || localStorage.getItem('mosqueId');
      let storedSubscriptionEnd = storedProfile.subscriptionEnd || localStorage.getItem('subscriptionEnd');
      let storedEtvUser = storedProfile.etvUser ?? (localStorage.getItem('etvUser') === "true");


      // 🕵️ Kalau data penting tiada, ambil dari Firestore
      console.warn("⚠️ Fetching latest user details from Firestore...");
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();

        syncEtikafAccount(user).catch((error) => {
          console.warn("Peranan akaun belum dapat diselaraskan:", error);
        });

        setIsAuthenticated(true);
        localStorage.setItem('isAuthenticated', 'true');

        storedMosqueId = userData.mosqueId || user.uid;
        storedSubscriptionEnd = userData.subscriptionEnd || null;
        storedEtvUser = userData.etvUser ?? false;

        const updatedProfile = {
          ...(storedProfile || {}),
          uid: user.uid,
          mosqueId: storedMosqueId,
          namaMasjid: userData.namaMasjid || "",
          emailMasjid: userData.emailMasjid || "",
          noTelAnda: userData.noTelAnda || "",
          etvUser: storedEtvUser,
          subscriptionEnd: storedSubscriptionEnd,
          approved: userData.approved ?? true,
          status: userData.status || "active",
        };

        localStorage.setItem("userProfile", JSON.stringify(updatedProfile));
        console.log("✅ Updated userProfile from Firestore:", updatedProfile);
      } else {
        console.warn("⚠️ User document not found in Firestore.");
        setIsAuthenticated(false);
        setSubscriptionValid(false);
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userProfile');
        setLoading(false);
        return;
      }

      // 🔹 Update State
      setMosqueId(storedMosqueId);
      setSubscriptionEnd(storedSubscriptionEnd);
      setEtvUser(storedEtvUser === true || storedEtvUser === "true");

      // ⏳ Check subscription validity
      if (storedSubscriptionEnd) {
        try {
          const expiryDate = new Date(storedSubscriptionEnd);
          if (!isNaN(expiryDate.getTime())) {
            const today = new Date();
            setSubscriptionValid(today < expiryDate);
          } else {
            console.error("❌ Invalid subscription date:", storedSubscriptionEnd);
            setSubscriptionValid(false);
          }
        } catch (err) {
          console.error("❌ Error parsing date:", err);
          setSubscriptionValid(false);
        }
      } else {
        console.warn("⚠️ No subscriptionEnd found.");
        setSubscriptionValid(false);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // ⏳ Auto-check subscription while user keeps app open
  useEffect(() => {
    if (!isAuthenticated || !subscriptionEnd) return;

    const checkSubscriptionExpiry = () => {
      const expiryDate = new Date(subscriptionEnd);
      const now = new Date();

      if (isNaN(expiryDate.getTime()) || now >= expiryDate) {
        console.warn("⛔ Subscription expired while app is open:", subscriptionEnd);
        setSubscriptionValid(false);
      }
    };

    checkSubscriptionExpiry();

    const interval = setInterval(checkSubscriptionExpiry, 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, subscriptionEnd]);


  useEffect(() => {
    if (!mosqueId) return;

    const masjidRef = doc(db, "masajid", mosqueId);

    const unsub = onSnapshot(
      masjidRef,
      (snap) => {
        const data = snap.data() || {};
        const enabled = !!data?.kempen?.kempenMode;
        setKempenMode(enabled);
        localStorage.setItem("kempenMode", enabled ? "true" : "false");
      },
      (err) => console.error("❌ kempenMode snapshot error:", err)
    );

    return () => unsub();
  }, [mosqueId]);


  // 🚪 Logout function
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsAuthenticated(false);
      localStorage.clear();
      setMosqueId(null);
      setSubscriptionValid(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // ⏳ Loading spinner
  if (loading || (isAuthenticated && !mosqueId)) {
    return (
      <div className="full-screen-container">
        <div className="spinner-container">
          <div className="spinner">
            <img src="/images/e.svg" alt="Logo" className="spinner-logo" />
          </div>
          <div className="spinner-border"></div>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >      <ErrorBoundary>
        <Routes>
          {/* 🟢 Login Page */}
          <Route
            path="/"
            element={
              isAuthenticated
                ? (subscriptionValid
                  ? <Navigate to="/dashboard" />
                  : <Navigate to="/subscription-expired" />)
                : <Login setIsAuthenticated={setIsAuthenticated} />
            }
          />

          <Route path="/daftar" element={<Daftar />} />
          <Route path="/status" element={<Status />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/subscription-expired" element={<SubscriptionExpired />} />
          <Route path="/result" element={<Result />} />

          {/* ✅ Protected Pages (Authenticated + Active Subscription) */}
          {isAuthenticated && subscriptionValid ? (
            <Route
              path="*"
              element={
                <Sidebar
                  isOpen={isOpen}
                  onToggle={toggleSidebar}
                  isMobile={isMobile}
                  onLogout={handleLogout}
                  kempenMode={kempenMode}
                >
                  <Routes>
                    <Route
                      path="/dashboard"
                      element={<Dashboard mosqueId={mosqueId} subscriptionEnd={subscriptionEnd} />}
                    />
                    <Route path="/kemas-kini" element={<SystemUpdates />} />
                    <Route path="/utama" element={<Utama isMobile={isMobile} subscriptionEnd={subscriptionEnd} />} />
                    <Route path="/profail" element={<Profail />} />
                    <Route path="/asatizah" element={<Asatizah isMobile={isMobile} />} />
                    <Route path="/kitab" element={<Kitab isMobile={isMobile} />} />
                    <Route path="/kempen" element={<Kempen />} />

                    <Route
                      path="/etv"
                      element={etvUser ? <Etv isMobile={isMobile} /> : <Navigate to="/noEtv" />}
                    />
                    <Route path="/noEtv" element={<NoEtv />} />
                    <Route path="/mesej" element={<Message isMobile={isMobile} mosqueId={mosqueId} />} />
                  </Routes>
                </Sidebar>
              }
            />
          ) : isAuthenticated && subscriptionValid === false ? (
            <Route path="*" element={<Navigate to="/subscription-expired" />} />
          ) : (
            <Route path="*" element={<Navigate to="/" />} />
          )}
        </Routes>
      </ErrorBoundary>
      <ToastContainer />
    </BrowserRouter>
  );
};

export default App;
